import React, { useImperativeHandle, useState } from 'react';
import { useSprings, animated } from 'react-spring';
import { GestureState, createPanResponder, gestureInitialState } from 'helpers/pan-responder';

interface InfinityCarouselProps {
  disableSwipe?: boolean;
  elementWidth: number;
  elementHeight: number;
  children: React.ReactNode;
  /** Spacing between elements in pixel */
  spacing?: number;
  /** Should be <= "children.length - 1" */
  startIndex?: number;
  onScrollHandler?: (direction: InfinityDirection) => void;
}

export interface ForwardRefProps {
  move: (direction: InfinityDirection, skipOnScrollCb?: boolean) => void;
}

export enum InfinityDirection {
  Next = 1,
  Prev = -1,
}

export const InfiniteScroll = React.forwardRef<ForwardRefProps, InfinityCarouselProps>((props, forwardedRef) => {
  const { children, elementHeight, elementWidth, disableSwipe, spacing = 0, startIndex = 0, onScrollHandler } = props;
  const [ gestureState, setGestureState ] = useState<GestureState>(gestureInitialState);
  const [ offset, setOffset ] = useState(startIndex);

  const childrenArray = React.Children.toArray(children);
  const arr = [ ...childrenArray, ...childrenArray, ...childrenArray ];
  const itemsCount = arr.length;
  const finalElementWidth = elementWidth + spacing;

  const springs = useSprings(
    itemsCount,
    arr.map((_, i) => {
      const { dx } = gestureState;
      let currentPosition = ((i - offset + itemsCount) % itemsCount) * -finalElementWidth + childrenArray.length * finalElementWidth;
      if (dx) {
        currentPosition += dx;
      }
      const immediate = !dx && currentPosition <= childrenArray.length * -finalElementWidth || currentPosition >= childrenArray.length * finalElementWidth;
      return {
        x: currentPosition,
        immediate,
        config: { tension: 180, friction: 20 },
      };
    })
  );

  const panHandlers = createPanResponder({
    onStart: (state) => setGestureState(state),
    onMove: (state) => {
      setGestureState(state);
    },
    onEnd: (s) => {
      setGestureState((prevGState: any) => {
        if (prevGState) {
          if (prevGState.dx > 0) scroll(InfinityDirection.Next);
          else if (prevGState.dx < 0)  scroll(InfinityDirection.Prev);
        }
        return {
          ...s,
          prevGState,
        };
      });
    },
  });

  useImperativeHandle(forwardedRef, () => ({
    move(d, skipOnScrollCb) {
      scroll(d, skipOnScrollCb);
    },
  }));

  const scroll = (newOffset: number, skipOnScrollCb?: boolean) => {
    if (onScrollHandler && !skipOnScrollCb) {
      onScrollHandler(newOffset);
    }
    setOffset((prevOffset) => (prevOffset + newOffset + itemsCount) % itemsCount);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', direction: 'rtl' }} {...(disableSwipe ? {} : panHandlers)}>
      <div
        style={{
          width: '100%',
          height: elementHeight,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {springs.map(({ x }, i) => (
          <animated.div
            key={i}
            style={{
              position: 'absolute',
              transform: x.to((xPos) => `translateX(${xPos}px)`),
            }}
          >
            {arr[i]}
          </animated.div>
        ))}
      </div>
    </div>
  );
});
